import * as z from "zod";
import { priceChannels } from "../catalog/enums";
import { numberedDocumentTypes, numberPattern, numberPrefix } from "../documents/numbering";
import { soStatuses } from "../sales/enums";
import {
  choice,
  codeRef,
  type DatasetGroup,
  dataset,
  day,
  emailAddress,
  flag,
  integer,
  nullable,
  percentage,
  ref,
  refs,
  text,
} from "./dataset";
import { erpInvoiceDirections, erpProductTypes, locationTypes, paymentCategories, taxDestinations } from "./enums";

// The reference data administered from Backstage (ADR-077): one entry per table of the `reference` schema
// (nexxt.dbml, reference section), with the fields named as its columns. `size_run` holds its sizes in order
// (`reference.size_run_size`); `archived` stands for `archived_at`.

const currencyCode = z.string().regex(/^[A-Z]{3}$/);
const countryCode = z.string().regex(/^[A-Z]{2}$/);
const incotermCode = z.string().regex(/^[A-Z]{3}$/);

const sortOrder = () => integer(0, 100_000, 0);

/** A time zone the runtime knows, e.g. `Europe/Rome`: the appointments of a showroom are shown in it. */
const timezone = () => {
  const zone = text(64);
  return { ...zone, schema: zone.schema.refine(isTimezone) };
};

function isTimezone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

const keyAndName = { key: text(60), name: text(100) };

/**
 * The type the items of a category, or of a subcategory before it, have in the file of the items to the ERP (V-16,
 * ADR-257): a style may override it.
 */
const withErpProductType = { ...keyAndName, erpProductType: nullable(choice(erpProductTypes)) };

export const datasets = {
  // --- campaign: what a sales campaign is made of, the CRM administrator's as on HubSpot (ADR-303) ---
  season: dataset({
    key: "season",
    group: "campaign",
    label: ["code"],
    fields: { code: text(20), name: text(100), year: nullable(integer(2000, 2100)), archived: flag() },
  }),
  category: dataset({ key: "category", group: "campaign", label: ["name"], fields: { name: text(100) } }),

  // --- product: what a linesheet line and its SKUs are made of ---
  gender: dataset({
    key: "gender",
    group: "product",
    label: ["name"],
    fields: { key: text(40), name: text(100) },
  }),
  product_category: dataset({
    key: "product_category",
    group: "product",
    label: ["name"],
    fields: withErpProductType,
  }),
  product_subcategory: dataset({
    key: "product_subcategory",
    group: "product",
    label: ["name"],
    fields: withErpProductType,
  }),
  // v0.2 §4 rule 5: the combinations the systems downstream know; a line is offered and accepts only these
  taxonomy_rule: dataset({
    key: "taxonomy_rule",
    group: "product",
    label: [],
    fields: {
      genderId: ref("gender"),
      productCategoryId: ref("product_category"),
      productSubcategoryId: nullable(ref("product_subcategory")),
      sizeRunCategoryId: nullable(ref("size_run_category")),
      sizeRunId: nullable(ref("size_run")),
    },
  }),
  size: dataset({
    key: "size",
    group: "product",
    label: ["code"],
    fields: { code: text(20), name: text(60), abbreviation: nullable(text(20)), sortOrder: sortOrder() },
  }),
  size_run_category: dataset({ key: "size_run_category", group: "product", label: ["name"], fields: keyAndName }),
  size_run: dataset({
    key: "size_run",
    group: "product",
    label: ["name"],
    fields: {
      key: text(100),
      name: text(200),
      sizeRunCategoryId: nullable(ref("size_run_category")),
      sizeIds: refs("size"),
    },
  }),
  colour: dataset({
    key: "colour",
    group: "product",
    label: ["name"],
    fields: { name: text(100), code2: nullable(text(2, /^.{2}$/)), code3: nullable(text(3, /^.{3}$/)) },
  }),
  colour_combination: dataset({
    key: "colour_combination",
    group: "product",
    label: ["name"],
    fields: {
      name: text(200),
      baseColourId: ref("colour"),
      secondaryColourId: nullable(ref("colour")),
      code: nullable(text(20)),
      variations: nullable(text(200)),
    },
  }),
  customs_classification: dataset({
    key: "customs_classification",
    group: "product",
    label: ["name"],
    fields: { code: nullable(text(40)), name: text(200) },
  }),
  weight_unit: dataset({
    key: "weight_unit",
    group: "product",
    label: ["code"],
    fields: { code: text(10), name: text(40) },
  }),

  // --- commercial: prices and terms of sale ---
  price_list: dataset({
    key: "price_list",
    group: "commercial",
    label: ["code"],
    fields: {
      code: text(40),
      channel: choice(priceChannels),
      region: nullable(text(20)),
      defaultCurrency: nullable(codeRef("currency", currencyCode)),
      sortOrder: sortOrder(),
    },
  }),
  payment_terms: dataset({
    key: "payment_terms",
    group: "commercial",
    label: ["name"],
    fields: {
      code: text(40),
      name: text(200),
      category: choice(paymentCategories),
      netDays: nullable(integer(0, 365)),
      depositPct: nullable(percentage()),
      paymentTiming: nullable(text(40)),
      hilldunTermCode: nullable(text(20)),
      description: nullable(text(2000)),
    },
  }),
  incoterm: dataset({
    key: "incoterm",
    group: "commercial",
    code: incotermCode,
    label: ["name"],
    // allocation rule R5: DDP 1, EXW 2, the others 3
    fields: { name: text(100), allocationRank: nullable(integer(1, 9)) },
  }),
  delivery_term: dataset({
    key: "delivery_term",
    group: "commercial",
    label: ["name"],
    fields: { code: text(40), name: text(200), incotermCode: nullable(codeRef("incoterm", incotermCode)) },
  }),
  showroom_location: dataset({
    key: "showroom_location",
    group: "commercial",
    label: ["name"],
    fields: { name: text(100), city: nullable(text(100)), timezone: timezone() },
  }),

  // B-23, ADR-236: the pipeline and the stage of HubSpot the deal of a sales order is at, by the status of the order
  hubspot_deal_stage: dataset({
    key: "hubspot_deal_stage",
    group: "commercial",
    label: ["soStatus"],
    fields: { soStatus: choice(soStatuses), pipeline: text(60), stage: text(60) },
  }),

  // --- company: who sells, from where, under which taxes ---
  currency: dataset({
    key: "currency",
    group: "company",
    code: currencyCode,
    label: ["name"],
    fields: { name: text(60), symbol: nullable(text(5)), decimals: integer(0, 4, 2) },
  }),
  country: dataset({
    key: "country",
    group: "company",
    code: countryCode,
    label: ["name"],
    fields: {
      name: text(100),
      iso3: nullable(text(3, /^[A-Z]{3}$/)),
      nativeName: nullable(text(100)),
      cultureCode: nullable(text(10)),
      currencyCode: nullable(codeRef("currency", currencyCode)),
      isEu: flag(),
      isVisible: flag(true),
    },
  }),
  legal_entity: dataset({
    key: "legal_entity",
    group: "company",
    label: ["name"],
    fields: {
      code: text(20),
      name: text(200),
      countryCode: codeRef("country", countryCode),
      baseCurrency: codeRef("currency", currencyCode),
      bcCompanyId: nullable(text(64)),
      allocationEnabled: flag(true),
      // ADR-178 §1.9, ADR-197: released without the invoice, invoiced on the units shipped (Tomorrow US)
      invoiceAfterShipment: flag(),
      // B-54 point 1, ADR-226: the account of BC every adjustment of stock of its warehouses is proposed with
      adjustmentGlAccount: nullable(text(20)),
      // C-01, ADR-254: what its documents print under its name, and who its emails answer to and copy
      vatNumber: nullable(text(30)),
      registeredAddress: nullable(text(300)),
      senderEmail: nullable(emailAddress()),
      depositCollectionEmail: nullable(emailAddress()),
      // C-40, V-14, ADR-257: the company gets the file of the items to the ERP, at every sync and resend
      erpItemFile: flag(),
      // C-49, ADR-258: the folder of the shared library the files to the ERP of the company go to
      erpFolder: nullable(text(400)),
    },
  }),
  location: dataset({
    key: "location",
    group: "company",
    label: ["name"],
    fields: {
      code: text(40),
      name: text(200),
      legalEntityId: ref("legal_entity"),
      type: choice(locationTypes),
      countryCode: codeRef("country", countryCode),
      threePl: nullable(text(60)),
      transferCustomerCode: nullable(text(40)),
      archived: flag(),
    },
  }),
  // the most specific match wins: legal entity × ship-from country × incoterm × destination
  tax_rule: dataset({
    key: "tax_rule",
    group: "company",
    label: ["taxCode"],
    fields: {
      taxCode: text(40),
      legalEntityId: ref("legal_entity"),
      shipFromCountryCode: nullable(codeRef("country", countryCode)),
      incotermCode: nullable(codeRef("incoterm", incotermCode)),
      destinationType: choice(taxDestinations, "any"),
      destinationCountryCode: nullable(codeRef("country", countryCode)),
      priority: integer(0, 10_000, 100),
      validFrom: nullable(day()),
      validTo: nullable(day()),
    },
  }),
  // C-48, ADR-255: how Nexxt numbers the invoices, or the credit notes, of a sale of a company, one series for each
  // year: the year is in the pattern, and documents keeps the counter of each year. `firstYear` and `firstNumber`: the
  // number the series starts from at the cutover, which Finance fixes; every year after starts from 1. TODO(D-25):
  // prefix and pattern agreed with Memento, to match the VAT registers of Business Central (B-108)
  document_number_series: dataset({
    key: "document_number_series",
    group: "company",
    label: ["prefix", "pattern"],
    fields: {
      legalEntityId: ref("legal_entity"),
      documentType: choice(numberedDocumentTypes),
      prefix: nullable({ ...text(10), schema: numberPrefix }),
      pattern: { ...text(40), schema: numberPattern, initial: "{prefix}{yyyy}-{n}" },
      digits: integer(1, 9, 6),
      timezone: { ...timezone(), initial: "Europe/Rome" },
      firstYear: nullable(integer(2026, 2100)),
      firstNumber: integer(1, 999_999_999, 1),
    },
  }),
  // spec v0.3 §7.4.3, D-25, ADR-256: the VAT codes of Business Central of a company and the rate each applies, which make
  // the VAT of the lines of the invoices file to the ERP. TODO(D-25): empty until Finance and Memento fix them (B-108)
  erp_vat_code: dataset({
    key: "erp_vat_code",
    group: "company",
    label: ["code"],
    fields: {
      legalEntityId: ref("legal_entity"),
      code: text(20, /^[^;\s]+$/),
      rate: percentage(),
      description: nullable(text(100)),
      purchaseScope: nullable(choice(taxDestinations)),
    },
  }),
  // spec v0.3 §7.4.2, D-25, ADR-256: the register (Protocol), the dimensions and the account of the lines that are not
  // goods of the invoices file, for the purchases and for the sales of a company. TODO(D-25): Finance and Memento (B-108)
  erp_invoice_setup: dataset({
    key: "erp_invoice_setup",
    group: "company",
    label: ["direction", "protocol"],
    fields: {
      legalEntityId: ref("legal_entity"),
      direction: choice(erpInvoiceDirections),
      protocol: text(20, /^[^;\s]+$/),
      costCentre: nullable(text(20, /^[^;\s]+$/)),
      project: nullable(text(20, /^[^;\s]+$/)),
      costCentreCode: nullable(text(20, /^[^;\s]+$/)),
      costAccount: nullable(text(20, /^[^;\s]+$/)),
    },
  }),
  // B-77, ADR-210: the one calendar of the working days, Monday to Friday without these; the gates escalate on it
  holiday: dataset({
    key: "holiday",
    group: "company",
    label: ["name"],
    fields: { day: day(), name: text(100) },
  }),
} as const;

export type DatasetKey = keyof typeof datasets;

export const datasetKeys = Object.keys(datasets) as [DatasetKey, ...DatasetKey[]];

/** Any of the datasets, as its own type: what a schema built for each of them distributes over. */
export type AnyDataset = (typeof datasets)[DatasetKey];

/**
 * Who changes the records of a dataset (ADR-178 §4.6, ADR-192): one permission per group. In the seed the product
 * data is Operations', seasons and categories the CRM administrator's and Operations' (ADR-303), the commercial and
 * company data (legal entities, tax rules, payment terms, currencies…) Finance's. The domain checks it on every
 * write; the app shows a dataset read-only to who lacks it.
 */
export const referenceManagePermissions = {
  product: "reference.product.manage",
  campaign: "reference.campaign.manage",
  commercial: "reference.commercial.manage",
  company: "reference.company.manage",
} as const satisfies Record<DatasetGroup, string>;

export const managePermissionOf = (dataset: DatasetKey): string => referenceManagePermissions[datasets[dataset].group];
